import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import ky from 'ky';
import { z } from 'zod';
import { InfoError } from '@salsita-ai/platform-common/utils';
import { round } from '../util/round.js';
import { QuoteLineDesignSpec, toInternalQuote, toInternalQuoteLine } from './metalworks-api.types.js';
export const metalworksApiUrls = {
    price: ()=>'api/metalworks/price/',
    quote: (quoteId)=>`api/metalworks/quotes/${quoteId}/`,
    quoteLine: (quoteId, quoteLineId)=>`api/metalworks/quotes/${quoteId}/lines/${quoteLineId}/`,
    quoteLines: (quoteId)=>`api/metalworks/quotes/${quoteId}/lines/`,
    quotes: ()=>'api/metalworks/quotes/'
};
export class MetalworksTokenMissingError extends Error {
    constructor(){
        super('Missing access token');
        this.name = 'MetalworksTokenMissingError';
    }
}
const priceSchema = z.object({
    price_ex: z.string()
});
const sanitizeDesignSpec = (designSpec)=>({
        ...designSpec,
        height: round(designSpec.height, 2),
        width: round(designSpec.width, 2)
    });
const buildFetchPrice = (metalworksFetch)=>async (params)=>{
        const { height, width, quantity, material, holes, numBends, powderCoating } = params;
        const response = await metalworksFetch({
            url: metalworksApiUrls.price(),
            searchParams: {
                height: round(height, 2),
                holes: JSON.stringify(holes),
                material,
                num_bends: numBends,
                powder_coating: powderCoating,
                quantity,
                width: round(width, 2)
            }
        }).json();
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { price_ex } = priceSchema.parse(response);
        const price = parseFloat(price_ex);
        if (isNaN(price)) {
            throw new Error(`Invalid price: ${price_ex}`);
        }
        return price;
    };
const buildListQuotes = (metalworksFetch)=>async ()=>{
        const response = await metalworksFetch({
            url: metalworksApiUrls.quotes(),
            searchParams: {
                limit: 100
            }
        }).json();
        return {
            count: response.count,
            next: response.next,
            previous: response.previous,
            results: response.results.map((q)=>camelcaseKeys(q))
        };
    };
const buildCreateQuote = (metalworksFetch)=>async (params)=>{
        const body = decamelizeKeys(params.data);
        const response = await metalworksFetch({
            method: 'POST',
            url: metalworksApiUrls.quotes(),
            json: body
        }).json();
        return camelcaseKeys(response);
    };
const buildCreateQuoteLine = (metalworksFetch)=>async (params)=>{
        const { quoteId, data } = params;
        const formData = new FormData();
        const sanitizedDesignSpec = sanitizeDesignSpec(data.designSpec);
        const designSpec = decamelizeKeys(sanitizedDesignSpec);
        // Validate data before sending it
        QuoteLineDesignSpec.parse(designSpec);
        formData.append('configuration_id', data.configurationId);
        formData.append('quantity', String(data.quantity));
        formData.append('drawing_file', data.drawingFile, 'drawing.pdf');
        formData.append('design_image', data.designImage, 'image.png');
        formData.append('configuration_read_token', data.configurationReadToken);
        formData.append('configuration_write_token', data.configurationWriteToken);
        formData.append('design_spec', JSON.stringify(designSpec));
        if (data.stepFile) {
            formData.append('step_file', data.stepFile, 'model.step');
        }
        if (data.usage) {
            formData.append('usage', data.usage);
        }
        const response = await metalworksFetch({
            method: 'POST',
            url: metalworksApiUrls.quoteLines(quoteId),
            body: formData
        }).json();
        return toInternalQuoteLine(response);
    };
const buildGetQuoteLine = (metalworksFetch)=>async (params)=>{
        const response = await metalworksFetch({
            url: metalworksApiUrls.quoteLine(params.quoteId, params.quoteLineId)
        }).json();
        return toInternalQuoteLine(response);
    };
const buildGetQuote = (metalworksFetch)=>async (params)=>{
        const response = await metalworksFetch({
            url: metalworksApiUrls.quote(params.quoteId)
        }).json();
        return toInternalQuote(response);
    };
const buildUpdateQuote = (metalworksFetch)=>async (params)=>{
        const { quoteId } = params;
        const body = decamelizeKeys(params.data);
        const response = await metalworksFetch({
            method: 'PATCH',
            url: metalworksApiUrls.quote(quoteId),
            json: body
        }).json();
        return camelcaseKeys(response);
    };
const buildUpdateQuoteLine = (metalworksFetch)=>async (params)=>{
        const { quoteId, quoteLineId, data } = params;
        const formData = new FormData();
        const designSpec = decamelizeKeys(params.data.designSpec);
        const parsedDesignSpec = QuoteLineDesignSpec.parse(designSpec);
        formData.append('configuration_id', data.configurationId);
        formData.append('quantity', String(data.quantity));
        formData.append('drawing_file', data.drawingFile, 'drawing.pdf');
        formData.append('design_image', data.designImage, 'image.png');
        formData.append('configuration_read_token', data.configurationReadToken);
        formData.append('configuration_write_token', data.configurationWriteToken);
        formData.append('design_spec', JSON.stringify(parsedDesignSpec));
        if (data.stepFile) {
            formData.append('step_file', data.stepFile, 'model.step');
        }
        if (data.usage) {
            formData.append('usage', data.usage);
        }
        const response = await metalworksFetch({
            method: 'PATCH',
            url: metalworksApiUrls.quoteLine(quoteId, quoteLineId),
            body: formData
        }).json();
        return toInternalQuoteLine(response);
    };
export const createMetalworksApiClient = (baseUrl, accessToken)=>{
    const metalworksFetch = ({ method = 'GET', url, searchParams = {}, headers = {}, body, json })=>{
        if (!accessToken) {
            throw new MetalworksTokenMissingError();
        }
        return ky(url, {
            method,
            searchParams,
            prefixUrl: baseUrl,
            headers: {
                ...headers,
                Authorization: `Bearer ${accessToken}`
            },
            json,
            body,
            hooks: {
                beforeError: [
                    async (err)=>{
                        if (err.response) {
                            const errorMessage = `${err.message} - ${await err.response.text()}`;
                            if (err.response.status === 401) {
                                // Log 401 errors as info messages, not errors
                                return Object.assign(new InfoError(errorMessage), {
                                    response: err.response,
                                    request: err.request,
                                    options: err.options
                                });
                            }
                            err.message = errorMessage;
                        }
                        return err;
                    }
                ]
            }
        });
    };
    return {
        fetchPrice: buildFetchPrice(metalworksFetch),
        listQuotes: buildListQuotes(metalworksFetch),
        createQuote: buildCreateQuote(metalworksFetch),
        createQuoteLine: buildCreateQuoteLine(metalworksFetch),
        getQuote: buildGetQuote(metalworksFetch),
        updateQuote: buildUpdateQuote(metalworksFetch),
        getQuoteLine: buildGetQuoteLine(metalworksFetch),
        updateQuoteLine: buildUpdateQuoteLine(metalworksFetch)
    };
};

//# sourceMappingURL=metalworks-api.js.map